/* TodayQuickActionSheets.jsx — 5 calm operational workflows.
 *
 * Each Quick Action on Today opens a small slide-over (right on
 * desktop · bottom-sheet on mobile) pre-filled with the smartest
 * default the data allows. Every submission posts a real activity
 * row so the live feed, intel rail and Today's stats refresh
 * instantly. Mobile-first. No full-page redirects.
 *
 * Heuristics (all soft — agents can switch the deal/recipient):
 *   • Chase Solicitor / Request Update  → most-overdue blocker first;
 *                                          fall back to longest-stale deal.
 *   • Send Seller Update                → broadcast to sellers across N
 *                                          active deals.
 *   • Update Milestone                  → deal closest to its target
 *                                          exchange; one-tap advance.
 *   • Add Note                          → smartly defaults to deal with
 *                                          the most recent activity.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle, Check, CheckCircle2, ChevronDown, ChevronRight,
  Inbox, Loader2, MessageSquare, Pencil, Sparkles, Send,
  ListChecks, Mail, Phone, Briefcase,
} from "lucide-react";
import { api } from "../../lib/api";
import { SlideOver } from "../dealroom/DealRoomSheets";
import { invalidateDealActivity } from "../../hooks/useDealActivity";
import { sendOperationalEmail, emailableRecipients } from "../../lib/emailClient";

/* ─── Participants fetch helper ──────────────────────────────────
 * Returns participants for a given deal id with simple in-memory
 * cache. Used by the operational sheets to resolve recipient emails
 * by role (e.g. "buyer_solicitor" → Priya Connell). */
const _participantsCache = new Map();
async function fetchParticipantsFor(dealId) {
  if (!dealId) return [];
  if (_participantsCache.has(dealId)) return _participantsCache.get(dealId);
  try {
    const { data } = await api.get(`/api/deals/${dealId}/participants-enriched`);
    const list = Array.isArray(data) ? data : [];
    _participantsCache.set(dealId, list);
    setTimeout(() => _participantsCache.delete(dealId), 60_000);
    return list;
  } catch {
    return [];
  }
}

function ownerRoleToParticipantRoles(ownerRole) {
  const r = String(ownerRole || "").toLowerCase();
  if (r.includes("solicitor_buyer")  || r === "buyer_solicitor")  return ["buyer_solicitor"];
  if (r.includes("solicitor_seller") || r === "seller_solicitor") return ["seller_solicitor"];
  if (r === "buyer")  return ["buyer"];
  if (r === "seller") return ["seller"];
  if (r.includes("broker") || r.includes("mortgage")) return ["mortgage_broker"];
  if (r.includes("solicitor")) return ["buyer_solicitor", "seller_solicitor"];
  return [];
}

function findRecipientsByRole(participants, roles) {
  if (!roles?.length) return [];
  const set = new Set(roles);
  return emailableRecipients(participants.filter((p) => set.has(p.role || p.participant_type)));
}

/* Inline calm error banner — preserves the agent's draft. */
function InlineError({ message, onDismiss, testid }) {
  return (
    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/60 px-3.5 py-2.5 flex items-start gap-2.5" role="alert" data-testid={testid}>
      <AlertCircle size={13} className="text-rose-500 mt-0.5 shrink-0" strokeWidth={2.3} />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-rose-900">Delivery didn't go through.</p>
        <p className="text-[11px] text-rose-700/90 mt-0.5 leading-snug">{message}</p>
        <p className="text-[11px] text-rose-600/90 mt-1">Your draft is preserved — tap Send to try again.</p>
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="w-6 h-6 rounded inline-flex items-center justify-center text-rose-500 hover:bg-rose-100 transition-colors shrink-0" aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

/* ─── Shared atoms (kept local to avoid cross-imports) ────────── */
function Label({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1.5">
      {children}
    </label>
  );
}
const inputCls =
  "w-full h-10 rounded-xl bg-white border border-slate-200 px-3 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition";
const textareaCls =
  "w-full rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition resize-none leading-relaxed";

function PrimaryButton({ children, onClick, disabled, loading, testid, icon: Icon = Send }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled || loading}
      className="h-10 px-4 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ background: "linear-gradient(135deg,#8B5CF6,#6366F1)", boxShadow: "0 8px 22px -10px rgba(99,102,241,0.55)" }}
      data-testid={testid}
    >
      {loading ? <Loader2 size={13} className="animate-spin" /> : <Icon size={12} strokeWidth={2.4} />}
      {children}
    </button>
  );
}
function GhostButton({ children, onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
      className="h-10 px-3.5 rounded-xl text-[12.5px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
      data-testid={testid}>
      {children}
    </button>
  );
}

/* Deal picker pill — used inside sheets so agents can override the
 * smart default without leaving the sheet. */
function DealPicker({ deals, value, onChange, testid }) {
  return (
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} pr-8 appearance-none`}
        data-testid={testid}
      >
        {deals.map((d) => (
          <option key={d.deal_id || d.id} value={d.deal_id || d.id}>
            {d.property_address || d.name || "Untitled transaction"}
          </option>
        ))}
      </select>
      <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}

const roleHuman = (r) => {
  const x = String(r || "").toLowerCase();
  if (x.includes("solicitor_buyer")  || x === "buyer_solicitor")  return "the buyer's solicitor";
  if (x.includes("solicitor_seller") || x === "seller_solicitor") return "the seller's solicitor";
  if (x === "buyer")  return "the buyer";
  if (x === "seller") return "the seller";
  if (x.includes("broker") || x.includes("mortgage")) return "the mortgage broker";
  if (x.includes("agent")) return "the agent";
  return "the responsible party";
};

/* ═══════════════════════════════════════════════════════════════
   1 · ChaseSolicitorSheet  (also handles "Request Update")
   ═══════════════════════════════════════════════════════════════ */
export function ChaseSolicitorSheet({ open, onClose, todayData, onDone, mode = "chase" }) {
  // Smart default: most-overdue solicitor blocker; fall back to most-overdue any.
  const candidates = useMemo(() => {
    const needs = todayData?.needs_attention || [];
    const solicitorFirst = needs.filter((n) => /solicitor/i.test(n.blocker?.owner_role || ""));
    const ordered = (solicitorFirst.length ? solicitorFirst : needs).slice().sort(
      (a, b) => (b.blocker?.age_days || 0) - (a.blocker?.age_days || 0),
    );
    return ordered;
  }, [todayData]);

  const [selectedKey, setSelectedKey] = useState("");
  const selected = useMemo(() => candidates.find((c) => `${c.deal_id}:${c.blocker?.id}` === selectedKey) || candidates[0] || null, [candidates, selectedKey]);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [recipients, setRecipients] = useState([]);

  // Resolve recipients by role when selection changes
  useEffect(() => {
    let alive = true;
    setError(null);
    if (!selected) { setRecipients([]); return undefined; }
    (async () => {
      const all = await fetchParticipantsFor(selected.deal_id);
      const roles = ownerRoleToParticipantRoles(selected.blocker?.owner_role);
      const matched = findRecipientsByRole(all, roles);
      if (alive) setRecipients(matched);
    })();
    return () => { alive = false; };
  }, [selected]);

  // Auto-prefill operational copy when the selection changes
  useEffect(() => {
    if (!selected) { setBody(""); setSubject(""); return; }
    const role = roleHuman(selected.blocker?.owner_role);
    const item = selected.blocker?.title || "Outstanding item";
    const days = selected.blocker?.age_days || 0;
    const where = selected.property_address || "this transaction";
    setSubject(`${item} — ${where}`);
    setBody(
      `Hi,\n\nQuick operational check from the Deal Room. ${item} has been outstanding for ${days} day${days === 1 ? "" : "s"} on ${where} — anything you can share on timings would really help us keep the chain moving.\n\nThanks!`,
    );
  }, [selected]);

  const send = useCallback(async () => {
    if (!selected || !body.trim() || sending) return;
    if (recipients.length === 0) {
      setError(`No email on file for ${roleHuman(selected.blocker?.owner_role)}.`);
      return;
    }
    setSending(true); setError(null);
    // Always log a chase event for the chase-ladder regardless of email outcome
    try {
      await api.post(`/api/deals/${selected.deal_id}/awaiting/chase`, {
        item_id: selected.blocker.id,
        title: selected.blocker.title,
        owner: selected.blocker.owner_role || null,
        days_waiting: selected.blocker.age_days || 0,
        send_email: false,
      });
    } catch (err) {
      if (err?.response?.status !== 429) {
        // soft-continue — email delivery is the primary action
      }
    }
    // Now actually deliver the email
    const res = await sendOperationalEmail({
      dealId: selected.deal_id, recipients, subject, body,
      kind: mode === "chase" ? "chase" : "request",
      titleOverride: mode === "chase"
        ? `Chase emailed · ${selected.blocker.title}`
        : `Update request emailed · ${selected.blocker.title}`,
    });
    setSending(false);
    if (res.ok) {
      invalidateDealActivity(selected.deal_id);
      toast.success(res.deliveredSummary);
      onDone?.();
      onClose?.();
    } else {
      setError(res.errorMessage || "Email delivery failed. Try again.");
    }
  }, [selected, body, subject, recipients, sending, mode, onDone, onClose]);

  const title = mode === "chase" ? "Chase a solicitor" : "Request an update";
  const subtitle = candidates.length === 0
    ? "No blocked transactions right now — nothing to chase."
    : "Pre-filled from the most-overdue blocker. Edit or change the transaction below.";

  return (
    <SlideOver
      open={open} onClose={onClose} title={title} subtitle={subtitle} testid={`qa-${mode}-sheet`}
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid={`qa-${mode}-cancel`}>Cancel</GhostButton>
          <PrimaryButton onClick={send} loading={sending} disabled={!selected || !body.trim()} testid={`qa-${mode}-send`} icon={Sparkles}>
            {mode === "chase" ? "Send chase" : "Send request"}
          </PrimaryButton>
        </div>
      )}
    >
      {candidates.length === 0 ? (
        <div className="rounded-2xl bg-emerald-50/40 border border-emerald-100 px-4 py-5 flex items-start gap-3" data-testid={`qa-${mode}-empty`}>
          <CheckCircle2 size={15} className="text-emerald-500 mt-0.5" strokeWidth={2.4} />
          <div>
            <p className="text-[13px] font-semibold text-slate-900">Nothing currently blocked.</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Enjoy the breathing room — chases will appear here as transactions slow.</p>
          </div>
        </div>
      ) : (
        <>
          <Label>Blocker</Label>
          <div className="relative">
            <select
              value={selectedKey || `${candidates[0].deal_id}:${candidates[0].blocker?.id}`}
              onChange={(e) => setSelectedKey(e.target.value)}
              className={`${inputCls} pr-8 appearance-none`}
              data-testid={`qa-${mode}-blocker`}
            >
              {candidates.map((c) => (
                <option key={`${c.deal_id}:${c.blocker?.id}`} value={`${c.deal_id}:${c.blocker?.id}`}>
                  {c.blocker?.title} · {c.property_address} · {c.blocker?.age_days || 0}d
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          {selected && (
            <div className="mt-3 px-3.5 py-2.5 rounded-xl bg-rose-50/50 border border-rose-100 text-[11.5px] text-slate-700 flex items-start gap-2">
              <AlertCircle size={11} className="text-rose-500 mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold text-slate-900">{selected.blocker.title}</span>
                {" · "}awaiting {roleHuman(selected.blocker.owner_role)} on{" "}
                <span className="font-semibold text-slate-900">{selected.property_address}</span>
                {" · "}<span className="tabular-nums">{selected.blocker.age_days || 0}d outstanding</span>
              </span>
            </div>
          )}

          {recipients.length > 0 ? (
            <div className="mt-3 text-[11.5px] text-slate-600">
              <span className="font-semibold text-slate-900">Sending to:</span>{" "}
              {recipients.map((r) => r.full_name || r.email).join(", ")}
            </div>
          ) : selected ? (
            <div className="mt-3 text-[11.5px] text-amber-700">
              No email on file for {roleHuman(selected.blocker.owner_role)} — the chase will still log to the timeline.
            </div>
          ) : null}

          <div className="mt-5">
            <Label htmlFor={`qa-${mode}-subject`}>Subject</Label>
            <input id={`qa-${mode}-subject`} className={inputCls}
              value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid={`qa-${mode}-subject`} />
          </div>

          <div className="mt-4">
            <Label htmlFor={`qa-${mode}-body`}>Message</Label>
            <textarea id={`qa-${mode}-body`} className={textareaCls} rows={7}
              value={body} onChange={(e) => setBody(e.target.value)}
              data-testid={`qa-${mode}-body`} />
          </div>

          {error && <InlineError message={error} onDismiss={() => setError(null)} testid={`qa-${mode}-error`} />}
        </>
      )}
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   2 · SendSellerUpdateSheet (broadcast to sellers across active deals)
   ═══════════════════════════════════════════════════════════════ */
export function SendSellerUpdateSheet({ open, onClose, deals = [], onDone }) {
  const activeDeals = useMemo(
    () => (deals || []).filter((d) => !d.archived && !["completed", "fallen_through"].includes(String(d.stage || "").toLowerCase())),
    [deals],
  );
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState("Weekly transaction update");
  const [body, setBody] = useState(
    "Hi,\n\nA quick operational update on where we are this week:\n\n• \n• \n• \n\nHappy to jump on a call if anything needs discussing.\n\nThanks!",
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(activeDeals.map((d) => d.id)));
      setError(null);
    }
  }, [open, activeDeals]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const send = useCallback(async () => {
    if (selectedIds.size === 0 || !subject.trim() || !body.trim() || sending) return;
    setSending(true); setError(null);
    let delivered = 0;
    let lastError = null;
    let totalRecipients = 0;
    for (const dealId of selectedIds) {
      const all = await fetchParticipantsFor(dealId);
      const sellers = findRecipientsByRole(all, ["seller"]);
      if (sellers.length === 0) continue;
      const res = await sendOperationalEmail({
        dealId, recipients: sellers, subject, body,
        kind: "seller_update",
      });
      if (res.ok) {
        delivered += 1;
        totalRecipients += res.recipientCount || sellers.length;
        invalidateDealActivity(dealId);
      } else {
        lastError = res.errorMessage || lastError;
      }
    }
    setSending(false);
    if (delivered > 0) {
      toast.success(`Seller update sent to ${totalRecipients} participant${totalRecipients === 1 ? "" : "s"} across ${delivered} transaction${delivered === 1 ? "" : "s"}`);
      onDone?.();
      onClose?.();
    } else if (lastError) {
      setError(lastError);
    } else {
      setError("None of the selected transactions have a seller email on file.");
    }
  }, [selectedIds, subject, body, sending, onDone, onClose]);

  return (
    <SlideOver
      open={open} onClose={onClose} title="Send seller update"
      subtitle="Sends a calm operational update to the seller on every selected transaction."
      testid="qa-seller-sheet" wide
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 tabular-nums">{selectedIds.size} of {activeDeals.length} selected</p>
          <div className="flex items-center gap-2">
            <GhostButton onClick={onClose} testid="qa-seller-cancel">Cancel</GhostButton>
            <PrimaryButton onClick={send} loading={sending} disabled={selectedIds.size === 0 || !body.trim()} testid="qa-seller-send" icon={Inbox}>
              Send to {selectedIds.size}
            </PrimaryButton>
          </div>
        </div>
      )}
    >
      <Label>Transactions</Label>
      <ul className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
        {activeDeals.map((d, i) => {
          const checked = selectedIds.has(d.id);
          return (
            <li key={d.id}>
              <button type="button" onClick={() => toggle(d.id)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors text-left ${checked ? "bg-violet-50" : "hover:bg-slate-50"}`}
                data-testid={`qa-seller-deal-${i}`}>
                <span className={`w-4 h-4 rounded inline-flex items-center justify-center shrink-0 ${checked ? "" : "border border-slate-300"}`}
                      style={checked ? { background: "linear-gradient(135deg,#8B5CF6,#6366F1)" } : undefined}>
                  {checked && <Check size={10} strokeWidth={3} className="text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold text-slate-900 truncate">{d.name || "Untitled deal"}</span>
                  <span className="block text-[10.5px] text-slate-500 truncate">{d.stage || "—"}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-4">
        <Label htmlFor="qa-seller-subject">Subject</Label>
        <input id="qa-seller-subject" className={inputCls}
          value={subject} onChange={(e) => setSubject(e.target.value)}
          data-testid="qa-seller-subject" />
      </div>
      <div className="mt-4">
        <Label htmlFor="qa-seller-body">Message</Label>
        <textarea id="qa-seller-body" className={textareaCls} rows={8}
          value={body} onChange={(e) => setBody(e.target.value)} data-testid="qa-seller-body" />
      </div>
      {error && <InlineError message={error} onDismiss={() => setError(null)} testid="qa-seller-error" />}
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   3 · UpdateMilestoneSheet
   ═══════════════════════════════════════════════════════════════ */
const STAGE_ORDER = [
  { id: "offer",          label: "Offer Accepted" },
  { id: "memorandum",     label: "Memorandum Issued" },
  { id: "instruction",    label: "Instruction Issued" },
  { id: "draft_contracts", label: "Draft Contracts" },
  { id: "searches",       label: "Searches Ordered" },
  { id: "searches_received", label: "Searches Returned" },
  { id: "enquiries",      label: "Enquiries Raised" },
  { id: "enquiries_answered", label: "Enquiries Answered" },
  { id: "mortgage_offer", label: "Mortgage Offer" },
  { id: "exchange_ready", label: "Exchange Ready" },
  { id: "exchanged",      label: "Exchanged" },
  { id: "completed",      label: "Completed" },
];
function nextStageAfter(stage) {
  const idx = STAGE_ORDER.findIndex((s) => s.id === String(stage || "").toLowerCase());
  if (idx === -1 || idx >= STAGE_ORDER.length - 1) return null;
  return STAGE_ORDER[idx + 1];
}

export function UpdateMilestoneSheet({ open, onClose, deals = [], onDone }) {
  const active = useMemo(
    () => (deals || []).filter((d) => !d.archived && !["completed", "fallen_through"].includes(String(d.stage || "").toLowerCase())),
    [deals],
  );
  // Smart default: deal closest to its target exchange
  const defaultDeal = useMemo(() => {
    return active.slice().sort((a, b) => {
      const ax = a.targetExchange ? Date.parse(a.targetExchange) : Infinity;
      const bx = b.targetExchange ? Date.parse(b.targetExchange) : Infinity;
      return ax - bx;
    })[0] || null;
  }, [active]);

  const [dealId, setDealId] = useState(defaultDeal?.id || "");
  useEffect(() => { if (open) setDealId(defaultDeal?.id || ""); }, [open, defaultDeal]);

  const deal = active.find((d) => d.id === dealId) || defaultDeal;
  const next = deal ? nextStageAfter(deal.stage) : null;
  const [advancing, setAdvancing] = useState(false);

  const advance = useCallback(async () => {
    if (!deal || !next || advancing) return;
    setAdvancing(true);
    try {
      // 1) Update the deal stage
      await api.patch(`/api/deals/${deal.id}`, { stage: next.id }).catch(async () => {
        await api.put(`/api/deals/${deal.id}`, { stage: next.id });
      });
      // 2) Emit a milestone_completed activity row
      await api.post(`/api/v2/deals/${deal.id}/activity`, {
        activity_type: "milestone_completed",
        title: `${next.label} reached`,
        description: `Advanced from ${deal.stage || "previous stage"} → ${next.id}.`,
        meta: { source: "today_quick_action" },
      }).catch(() => {});
      invalidateDealActivity(deal.id);
      toast.success(`${deal.name} → ${next.label}`);
      onDone?.();
      onClose?.();
    } catch {
      toast.error("Could not advance milestone");
    } finally {
      setAdvancing(false);
    }
  }, [deal, next, advancing, onDone, onClose]);

  return (
    <SlideOver
      open={open} onClose={onClose} title="Update milestone"
      subtitle={active.length === 0 ? "No active transactions to advance." : "Advances the selected transaction to its next operational stage."}
      testid="qa-milestone-sheet"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid="qa-milestone-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={advance} loading={advancing} disabled={!deal || !next} testid="qa-milestone-advance" icon={ListChecks}>
            {next ? `Advance · ${next.label}` : "Already at final stage"}
          </PrimaryButton>
        </div>
      )}
    >
      {active.length === 0 ? (
        <p className="text-[12.5px] text-slate-500 px-1 py-2">No active transactions in this workspace yet.</p>
      ) : (
        <>
          <Label>Transaction</Label>
          <DealPicker deals={active} value={dealId} onChange={setDealId} testid="qa-milestone-deal" />

          {deal && (
            <div className="mt-5 rounded-2xl bg-white border border-slate-100 p-4" data-testid="qa-milestone-preview"
                 style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-400">Stage transition</p>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-400">From</p>
                  <p className="text-[13px] font-semibold text-slate-700 mt-1 truncate">
                    {STAGE_ORDER.find((s) => s.id === String(deal.stage || "").toLowerCase())?.label || deal.stage || "—"}
                  </p>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-violet-600">To</p>
                  <p className="text-[13px] font-semibold mt-1 truncate" style={{ color: "#5B21B6" }}>
                    {next ? next.label : "Final stage reached"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </SlideOver>
  );
}

/* ═══════════════════════════════════════════════════════════════
   4 · AddNoteSheet
   ═══════════════════════════════════════════════════════════════ */
export function AddNoteSheet({ open, onClose, deals = [], onDone }) {
  const active = useMemo(
    () => (deals || []).filter((d) => !d.archived && !["completed", "fallen_through"].includes(String(d.stage || "").toLowerCase())),
    [deals],
  );
  // Default to most-recently-touched
  const defaultDeal = useMemo(() => active.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0] || null, [active]);

  const [dealId, setDealId] = useState("");
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setDealId(defaultDeal?.id || ""); setBody(""); setInternal(true); } }, [open, defaultDeal]);

  const save = useCallback(async () => {
    const targetId = dealId || defaultDeal?.id;
    if (!targetId || !body.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/api/v2/deals/${targetId}/activity`, {
        activity_type: "note",
        title: body.split("\n")[0].slice(0, 100),
        description: body,
        meta: { internal, source: "today_quick_action" },
      });
      invalidateDealActivity(targetId);
      toast.success("Note saved");
      onDone?.();
      onClose?.();
    } catch {
      toast.error("Could not save note");
    } finally {
      setSaving(false);
    }
  }, [dealId, defaultDeal, body, internal, saving, onDone, onClose]);

  return (
    <SlideOver
      open={open} onClose={onClose} title="Add note"
      subtitle="Logged to the transaction timeline as an internal note."
      testid="qa-note-sheet"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <GhostButton onClick={onClose} testid="qa-note-cancel">Cancel</GhostButton>
          <PrimaryButton onClick={save} loading={saving} disabled={!body.trim()} testid="qa-note-save" icon={Pencil}>
            Save note
          </PrimaryButton>
        </div>
      )}
    >
      {active.length === 0 ? (
        <p className="text-[12.5px] text-slate-500 px-1 py-2">No active transactions in this workspace yet.</p>
      ) : (
        <>
          <Label>Transaction</Label>
          <DealPicker deals={active} value={dealId || defaultDeal?.id || ""} onChange={setDealId} testid="qa-note-deal" />

          <div className="mt-4">
            <Label htmlFor="qa-note-body">Note</Label>
            <textarea id="qa-note-body" className={textareaCls} rows={8}
              value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="What should the next person reading this know?"
              data-testid="qa-note-body" />
          </div>

          <label className="mt-3 flex items-center gap-2.5 cursor-pointer" data-testid="qa-note-internal-wrap">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
              className="w-4 h-4 accent-violet-600" data-testid="qa-note-internal" />
            <span className="text-[12.5px] text-slate-700 font-medium">Keep internal (not visible to participants)</span>
          </label>
        </>
      )}
    </SlideOver>
  );
}
